import React from "react";
import { Crosshair, Zap, Package } from "lucide-react";
import { loadoutIsEmpty } from "@/lib/loadout";

/**
 * Full loadout strip (used below the single-operator reveal).
 * Renders one row per enabled / present field. If nothing set, returns null.
 */
export const LoadoutStrip = ({ loadout, side, label, testId }) => {
  if (loadoutIsEmpty(loadout)) return null;
  const accent = side === "attacker" ? "#E67300" : "#1F88E8";

  const rows = [
    { key: "primary", label: "Primary", value: loadout.primary, Icon: Crosshair },
    { key: "secondary", label: "Secondary", value: loadout.secondary, Icon: Zap },
    { key: "gadget", label: "Gadget", value: loadout.gadget, Icon: Package },
  ].filter((r) => r.value);

  return (
    <div
      data-testid={testId || "loadout-strip"}
      className="border bg-neutral-950/60 divide-y divide-neutral-800/80"
      style={{ borderColor: `${accent}55` }}
    >
      {label && (
        <div
          className="px-4 py-2 text-[10px] tracking-[0.3em] uppercase font-bold"
          style={{ color: accent, fontFamily: '"JetBrains Mono", monospace' }}
        >
          {label}
        </div>
      )}
      {rows.map(({ key, label: k, value, Icon }) => (
        <div
          key={key}
          data-testid={`loadout-row-${key}`}
          className="flex items-start gap-4 px-4 py-3"
        >
          <div
            className="flex items-center gap-2 w-28 shrink-0 text-[10px] tracking-[0.3em] uppercase font-bold text-neutral-400 pt-0.5"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            <Icon size={12} style={{ color: accent }} /> {k}
          </div>
          <div
            className="flex-1 text-white text-sm sm:text-base font-semibold leading-snug"
            style={{ fontFamily: '"Manrope", sans-serif' }}
          >
            {value}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Compact labelled loadout used inside team mini-cards.
 * Stacks Primary / Secondary / Gadget on their own short lines.
 */
export const LoadoutBrief = ({ loadout, side }) => {
  if (loadoutIsEmpty(loadout)) return null;
  const accent = side === "attacker" ? "#E67300" : "#1F88E8";

  const rows = [
    { label: "PRIMARY", value: loadout.primary },
    { label: "SECONDARY", value: loadout.secondary },
    { label: "GADGET", value: loadout.gadget },
  ].filter((r) => r.value);
  if (rows.length === 0) return null;

  return (
    <div
      data-testid="loadout-brief"
      className="mt-2 w-full border-t pt-2 px-1 space-y-1 text-left"
      style={{ borderColor: `${accent}33` }}
    >
      {rows.map((r) => (
        <div key={r.label} className="leading-snug">
          <div
            className="text-[9px] tracking-[0.25em] font-bold"
            style={{ color: accent, fontFamily: '"JetBrains Mono", monospace' }}
          >
            {r.label}
          </div>
          <div
            className="text-[11px] text-white font-semibold"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            {r.value}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LoadoutStrip;
